import type { PropsWithChildren } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { DSC } from '@mentimeter/ragnar-dsc';
import { alpha } from '@mentimeter/ragnar-colors';
import { SlidePadding } from './SlidePadding';
import { SideBySideImage } from './SideBySideImage';
import type { LayoutSize } from './types';

const Wrap = ({ children }: { children: React.ReactNode }) => (
  <Box flex="1 1 auto" width="100%" height="100%">
    {children}
  </Box>
);

const extend =
  (contentImage: string) =>
  ({ theme }: { theme: DSC }) => ({
    backgroundColor: contentImage
      ? 'transparent'
      : alpha(theme.colors.neutralWeak, 0.5),
    backgroundImage: `url(${contentImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  });

interface SlideImageLayoutProps {
  image: {
    url: string;
    altText: string | undefined;
  };
  hasImageInset: boolean;
  snapDirection: 'x' | 'y';
  imagePosition: 'start' | 'end';
  layoutSize: LayoutSize;
}

export const PartialImageLayout = ({
  image,
  hasImageInset,
  snapDirection,
  imagePosition,
  layoutSize,
  children,
}: PropsWithChildren<SlideImageLayoutProps>) => {
  if (hasImageInset) {
    return (
      <SlidePadding>
        <SideBySideImage
          imageUrl={image.url}
          imageAltText={image.altText}
          imagePosition={imagePosition}
          imageWidth={layoutSize}
        >
          {children}
        </SideBySideImage>
      </SlidePadding>
    );
  }

  const isHorizontal = snapDirection === 'x';

  if (isHorizontal) {
    return (
      <Box
        width="100%"
        height="100%"
        flexDirection={imagePosition === 'start' ? 'row' : 'row-reverse'}
      >
        <Box
          role="img"
          aria-label={image.altText}
          width={layoutSize}
          height="100%"
          data-testid="content-image-horizontal"
          extend={extend(image.url)}
        />
        {/* Children needs to be rendered after the image in order to be able to be displayed
        on top of it, using zindex. (needed for open ended screenshots) */}
        <Wrap>
          <SlidePadding>{children}</SlidePadding>
        </Wrap>
      </Box>
    );
  } else {
    return (
      <Box
        width="100%"
        height="100%"
        flexDirection={imagePosition === 'start' ? 'column' : 'column-reverse'}
      >
        <Box
          role="img"
          aria-label={image.altText}
          width="100%"
          height={layoutSize}
          extend={extend(image.url)}
          data-testid="content-image-vertical"
        />
        {/* Children needs to be rendered after the image in order to be able to be displayed
        on top of it, using zindex. (needed for open ended screenshots) */}
        <Wrap>
          <SlidePadding>{children}</SlidePadding>
        </Wrap>
      </Box>
    );
  }
};
